import { JWT } from 'next-auth/jwt';

import { EnvironmentVariable, getEnvVar } from '@lib/config';
import { failingFetcher } from '@lib/http/fetch';
import { Maybe } from '@lib/types/Maybe';
import { Sale } from '@lib/types/Sale';
import { Asset } from '@lib/types/api/Asset';
import {
  CheckEntitlementRequest,
  CheckEntitlementResponse,
  ClaimUpmEntitlementRequest,
  ClaimUpmEntitlementResponse,
  SearchProductEntitlementsRequest,
  SearchProductsEntitlementsResponse,
} from '@lib/types/api/Entitlement';
import { Organization } from '@lib/types/api/Organization';
import {
  GetPackageVersionsRequest,
  GetPackageVersionsResponse,
} from '@lib/types/api/Package';
import { GetProductRequest, GetProductResponse } from '@lib/types/api/Product';
import {
  AddProductListItemV2Request,
  CreateProductListV2Request,
  GetProductListItemsV2Params,
  GetProductListItemsV2Response,
  GetProductListsV2Params,
  GetProductListsV2Response,
  LookupProductListsResponse,
  ProductListItemV2,
  ProductListV2,
  UpdateProductListV2Request,
} from '@lib/types/api/ProductListV2';
import {
  GetProductPreviewRequest,
  GetProductPreviewResponse,
  GetReportReviewResponse,
} from '@lib/types/api/ProductPreview';
import { GetPublisherRequest, Publisher } from '@lib/types/api/Publisher';
import {
  GetPublisherListsRequest,
  GetPublisherListsResponse,
} from '@lib/types/api/PublisherList';
import {
  CreateReviewRequest,
  DeleteReviewResponse,
  GetProductReviewOptions,
  GetProductReviewResponse,
  GetProductReviewStatisticsResponse,
  GetProductReviewStatisticsV2Response,
  GetReviewActionsRequest,
  GetReviewActionsResponse,
  GetReviewResponse,
  ListReviewsOptions,
  ListReviewsResponse,
  Review,
  UpdateReviewRequest,
  UpdateReviewResponse,
} from '@lib/types/api/Review';
import { GetUserPublicListsResponse, UserList } from '@lib/types/api/UserList';
import { UserProfile } from '@lib/types/api/UserProfile';
import { UserDetails } from '@lib/types/api/user/UserDetails';
import { buildQueryString } from '@lib/util/queryString';
import { trace } from '@opentelemetry/api';
import { encode } from 'base-64';

export type ClientOptions = {
  baseUrl?: string;
  dynamicBackend?: string;
  jwt?: Maybe<JWT>;
};

const SPAN_PADDING = 2;
const SPAN_PAD_CHAR = '0';

const REVALIDATE_ONE_HOUR_IN_SECONDS = 3600;

type GetHeadersOptions = {
  dynamicBackend?: string;
  useServiceAccount?: boolean;
  jwt?: Maybe<JWT>;
};

const getHeaders = (options: GetHeadersOptions) => {
  const headers = {};

  if (options.dynamicBackend) {
    headers['x-backend-host'] = options.dynamicBackend;
  }

  if (options.jwt && options.jwt['unity_token']) {
    headers['Authorization'] = `Bearer ${options.jwt['unity_token']}`;
  } else if (options.useServiceAccount) {
    const username = getEnvVar(EnvironmentVariable.usgApiUsername);
    const password = getEnvVar(EnvironmentVariable.usgApiPassword);
    const credentials = `${username}:${password}`;
    headers['Authorization'] = `Basic ${encode(credentials)}`;
  }

  const activeSpan = trace.getActiveSpan();
  if (activeSpan) {
    const spanContext = activeSpan.spanContext();
    headers['traceparent'] = `00-${spanContext.traceId}-${
      spanContext.spanId
    }-${spanContext.traceFlags
      .toString()
      .padStart(SPAN_PADDING, SPAN_PAD_CHAR)}`;
  }

  return headers;
};

export function createMarketplaceClient(options?: ClientOptions) {
  const jwt = options?.jwt;
  const genesisUser = jwt ? jwt['genesis_user'] : '';
  const baseUrl =
    options?.baseUrl ?? `${getEnvVar(EnvironmentVariable.usgApi)}/marketplace`;
  const dynamicBackend =
    options?.dynamicBackend ?? getEnvVar(EnvironmentVariable.dynamicBackendApi);

  const getAssets = async () => {
    return failingFetcher(`${baseUrl}/assets/v1`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
          jwt,
        }),
      },
    });
  };

  const getUserAccount = async (): Promise<UserDetails> => {
    return failingFetcher(`${baseUrl}/users/v1/${genesisUser}/`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
    });
  };

  const getNotifications = async (
    locale: string,
    page: number,
    rows: number
  ) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/users/${genesisUser}?locale=${locale}&page=${page}&rows=${rows}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getUnreadNotificationsCount = async (locale: string) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/users/${genesisUser}/unread-count?locale=${locale}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const markNotificationAsRead = async (
    locale: string,
    notificationId: string
  ) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/${notificationId}/users/${genesisUser}?locale=${locale}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const markAllNotificationAsRead = async (
    locale: string,
    notificationIds: string
  ) => {
    return failingFetcher(
      `${baseUrl}/notifications/v1/users/${genesisUser}?locale=${locale}&notificationIds=${notificationIds}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getUserProfile = async (userId: string): Promise<UserProfile> => {
    return failingFetcher(`${baseUrl}/users/v1/${userId}`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
    });
  };

  const getUserPublicLists = async (
    userId: string,
    start: number,
    count: number
  ): Promise<UserList[]> => {
    const response: GetUserPublicListsResponse = await failingFetcher(
      `${baseUrl}/productlist/v1/users/${userId}?start=${start}&count=${count}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );

    return response.results;
  };

  const getUserLists = async (
    params?: GetProductListsV2Params
  ): Promise<GetProductListsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getUserList = async (listId: string): Promise<ProductListV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists/${listId}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getUserListItems = async (
    listId: string,
    params?: GetProductListItemsV2Params
  ): Promise<GetProductListItemsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists/${listId}/items${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const createUserList = async (
    data: CreateProductListV2Request
  ): Promise<ProductListV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const updateUserList = async (
    listId: string,
    data: UpdateProductListV2Request
  ): Promise<ProductListV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists/${listId}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const deleteUserList = async (listId: string): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists/${listId}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const addItemToUserList = async (
    listId: string,
    data: AddProductListItemV2Request
  ): Promise<ProductListItemV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists/${listId}/items`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const removeItemFromUserList = async (
    listId: string,
    namespace: string,
    productId: string
  ): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists/${listId}/items/${namespace}/${productId}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getAsset = async (
    assetId: string,
    currency: string,
    locale: string
  ): Promise<Asset> => {
    return failingFetcher(
      `${baseUrl}/assets/v1/${assetId}?currency=${currency}&locale=${locale}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        cache: 'no-cache',
      }
    );
  };

  const getPublisher = async ({
    publisherId,
  }: GetPublisherRequest): Promise<Publisher> => {
    return failingFetcher(`${baseUrl}/publisher/v1/profile/${publisherId}`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const getPublisherLists = async ({
    start,
    count,
    organizationId,
  }: GetPublisherListsRequest): Promise<GetPublisherListsResponse> => {
    const params = {
      start,
      count,
    };

    const queryString = buildQueryString(params);

    return failingFetcher(
      `${baseUrl}/productlist/v1/publishers/${organizationId}${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );
  };

  const getListBySlug = async (slug: string): Promise<ProductListV2> => {
    return failingFetcher(`${baseUrl}/productlist/v2/lists/${slug}/slug`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          jwt,
        }),
      },
    });
  };

  const getOrganizationLists = async (
    organizationId: string,
    params?: GetProductListsV2Params
  ): Promise<GetProductListsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getOrganizationList = async (
    organizationId: string,
    listId: string
  ): Promise<ProductListV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists/${listId}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getOrganizationListItems = async (
    organizationId: string,
    listId: string,
    params?: GetProductListItemsV2Params
  ): Promise<GetProductListItemsV2Response> => {
    const queryString = buildQueryString(params ?? {});
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists/${listId}/items${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const createOrganizationList = async (
    organizationId: string,
    data: CreateProductListV2Request
  ): Promise<ProductListV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const updateOrganizationList = async (
    organizationId: string,
    listId: string,
    data: UpdateProductListV2Request
  ): Promise<ProductListV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists/${listId}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const deleteOrganizationList = async (
    organizationId: string,
    listId: string
  ): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists/${listId}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const addItemToOrganizationList = async (
    organizationId: string,
    listId: string,
    data: AddProductListItemV2Request
  ): Promise<ProductListItemV2> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists/${listId}/items`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const removeItemFromOrganizationList = async (
    organizationId: string,
    listId: string,
    namespace: string,
    productId: string
  ): Promise<void> => {
    return failingFetcher(
      `${baseUrl}/productlist/v2/organizations/${organizationId}/lists/${listId}/items/${namespace}/${productId}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const getPublisherByOrganizationId = async (
    organizationId: string
  ): Promise<Publisher> => {
    return failingFetcher(
      `${baseUrl}/publisher/v1/organization/${organizationId}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );
  };

  const getSalesForAsset = async (
    assetId: string
  ): Promise<{ sales?: Sale[] }> => {
    return failingFetcher(`${baseUrl}/sales/v1/products/${assetId}`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const getUserReviews = async (userId: string): Promise<Review[]> => {
    const response = await failingFetcher(
      `${baseUrl}/reviews/v1/users/${userId}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
      }
    );

    return response.reviews;
  };

  const getLoggedInUsersOrganizations = async (): Promise<{
    organizations: Organization[];
  }> => {
    return failingFetcher(`${baseUrl}/users/v1/${genesisUser}/organizations`, {
      method: 'GET',
      next: { revalidate: REVALIDATE_ONE_HOUR_IN_SECONDS },
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
    });
  };

  const getLoggedInUsersCartItems = async () => {
    return failingFetcher(`${baseUrl}/carts/v1/users/${genesisUser}`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          jwt,
        }),
      },
    });
  };

  const getAnonymousUsersCartItem = async (deviceId: string) => {
    return failingFetcher(`${baseUrl}/carts/v1/devices/${deviceId}`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
    });
  };

  const createDevice = async () => {
    return failingFetcher(`${baseUrl}/device/v1`, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
    });
  };

  const getUsersEntitlements = async (page = 1, rows = 100) => {
    return failingFetcher(
      `${baseUrl}/entitlements/v1/users/${genesisUser}/assetstore?rows=${rows}&page=${page}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const mergeDeviceCartIntoUserCart = async (deviceId?: string) => {
    if (!genesisUser || !deviceId) {
      return null;
    }
    return failingFetcher(
      `${baseUrl}/carts/v1/users/${genesisUser}/merge/${deviceId}`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
        },
      }
    );
  };

  const postNewsletterSignup = async (email: string, country = 'US') => {
    return failingFetcher(`${baseUrl}/users/v1/newsletter/assetstore`, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, country }),
    });
  };

  const getReviewsForProduct = (
    productId: string,
    productReviewsOptions: GetProductReviewOptions
  ): Promise<GetProductReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v1/products/${productId}?${new URLSearchParams(productReviewsOptions).toString()}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        next: { revalidate: REVALIDATE_ONE_HOUR_IN_SECONDS },
      }
    );
  };

  const getReviewStatisticsForProduct = (
    productId: string
  ): Promise<GetProductReviewStatisticsResponse> => {
    return failingFetcher(`${baseUrl}/reviews/v1/products/${productId}/stats`, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const getReviewStatisticsForProductV2 = (
    productId: string
  ): Promise<GetProductReviewStatisticsV2Response> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${productId}/reviews/stats`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
        },
        cache: 'no-cache',
      }
    );
  };

  const createReview = (
    productId: string,
    data: CreateReviewRequest
  ): Promise<GetReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${productId}/reviews`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const updateReview = (
    productId: string,
    reviewId: string,
    data: UpdateReviewRequest
  ): Promise<UpdateReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${productId}/reviews/${reviewId}`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify(data),
      }
    );
  };

  const deleteReview = (
    productId: string,
    reviewId: string
  ): Promise<DeleteReviewResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${productId}/reviews/${reviewId}`,
      {
        method: 'DELETE',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      }
    );
  };

  const listReviews = (
    options: ListReviewsOptions
  ): Promise<ListReviewsResponse> => {
    const { productId, sort, filterRating, start, count } = options;
    const effectiveSort = sort ?? 'recent';
    const effectiveCount = count ?? 10;
    const effectiveStart = start ?? 0;

    const params = {
      sort: effectiveSort,
      ...(filterRating && { filterRating: String(filterRating) }),
      start: String(effectiveStart),
      count: String(effectiveCount),
    };

    const queryString = buildQueryString(params);

    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${productId}/reviews?${queryString}`,
      {
        method: 'GET',
        headers: getHeaders({ jwt, dynamicBackend }),
      }
    );
  };

  const getReviewActions = ({
    productId,
    reviewIds,
  }: GetReviewActionsRequest): Promise<GetReviewActionsResponse> => {
    return failingFetcher(
      `${baseUrl}/reviews/v2/products/${productId}/reviews/actions`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            jwt,
          }),
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
          ...(reviewIds && { reviewIds }),
        }),
      }
    );
  };

  const getProduct = async ({
    productId,
    locale,
  }: GetProductRequest): Promise<GetProductResponse> => {
    const params = {
      locale,
    };

    const queryString = buildQueryString(params);

    const url = `${baseUrl}/products/commerce/v1/products/${productId}${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const getProductPreview = async ({
    productId,
    locale,
    expand = [],
  }: GetProductPreviewRequest): Promise<GetProductPreviewResponse> => {
    const _params = {
      locale,
      expand,
    };

    const queryString = buildQueryString(_params);

    const url = `${baseUrl}/products/commerce/v1/products/${productId}/preview${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          jwt,
        }),
      },
      cache: 'no-cache',
    });
  };

  const getPackageVersions = async ({
    packageName,
    locale,
    status = 'PUBLISHED',
  }: GetPackageVersionsRequest): Promise<GetPackageVersionsResponse> => {
    const params = {
      locale,
      status,
      sortBy: 'version',
      orderBy: 'DESC',
    };

    const queryString = buildQueryString(params);

    const url = `${baseUrl}/packages/v1/upm/${packageName}/package-versions${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const claimUpmEntitlement = (
    request: ClaimUpmEntitlementRequest
  ): Promise<ClaimUpmEntitlementResponse> => {
    return failingFetcher(`${baseUrl}/entitlements/v1/entitlements/claim/upm`, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...request, userId: genesisUser }),
    });
  };

  const checkEntitlement = (
    request: CheckEntitlementRequest
  ): Promise<CheckEntitlementResponse> => {
    return failingFetcher(
      `${baseUrl}/entitlements/v1/entitlements/check?expand=entitlement`,
      {
        method: 'POST',
        headers: {
          ...getHeaders({
            dynamicBackend,
            useServiceAccount: true,
          }),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: request.productId,
          userId: genesisUser,
        }),
      }
    );
  };

  const searchProductEntitlements = async ({
    sources,
    ...queryParams
  }: SearchProductEntitlementsRequest): Promise<SearchProductsEntitlementsResponse> => {
    const params = {
      ...queryParams,
      ...(sources && { sources: sources.join(',') }),
    };

    const queryString = buildQueryString(params);

    const url = `${baseUrl}/entitlements/v1/users/${genesisUser}/entitlements/search${queryString ? `?${queryString}` : ''}`;

    return failingFetcher(url, {
      method: 'GET',
      headers: {
        ...getHeaders({
          dynamicBackend,
          useServiceAccount: true,
        }),
      },
      cache: 'no-cache',
    });
  };

  const reportReview = async (
    productId: string,
    reviewId: string,
    reason: string,
    type: string
  ): Promise<GetReportReviewResponse> => {
    const url = `${baseUrl}/reviews/v2/products/${productId}/reviews/${reviewId}/report`;
    return failingFetcher(url, {
      method: 'POST',
      headers: {
        ...getHeaders({
          dynamicBackend,
          jwt,
        }),
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({
        reason,
        ...(type && { type: type.toLowerCase() }),
      }),
    });
  };

  const lookupProductLists = async (
    productIds: string[]
  ): Promise<LookupProductListsResponse> => {
    const queryString = buildQueryString({
      'product-ids': productIds.join(','),
    });
    return failingFetcher(
      `${baseUrl}/productlist/v2/users/${genesisUser}/lists/memberships${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: {
          ...getHeaders({ dynamicBackend, jwt }),
        },
      }
    );
  };

  return {
    jwt,
    getAssets,
    getUserAccount,
    getNotifications,
    getUnreadNotificationsCount,
    markNotificationAsRead,
    markAllNotificationAsRead,
    getUserProfile,
    getUserPublicLists,
    getAsset,
    getPublisher,
    getPublisherLists,
    getUserLists,
    getUserList,
    getUserListItems,
    createUserList,
    updateUserList,
    deleteUserList,
    addItemToUserList,
    removeItemFromUserList,
    getListBySlug,
    getOrganizationLists,
    getOrganizationList,
    getOrganizationListItems,
    createOrganizationList,
    updateOrganizationList,
    deleteOrganizationList,
    addItemToOrganizationList,
    removeItemFromOrganizationList,
    getPublisherByOrganizationId,
    getUserReviews,
    getLoggedInUsersOrganizations,
    getLoggedInUsersCartItems,
    mergeDeviceCartIntoUserCart,
    getAnonymousUsersCartItem,
    getUsersEntitlements,
    searchProductEntitlements,
    createDevice,
    postNewsletterSignup,
    getSalesForAsset,
    getReviewsForProduct,
    getReviewStatisticsForProduct,
    getReviewStatisticsForProductV2,
    createReview,
    updateReview,
    deleteReview,
    listReviews,
    getReviewActions,
    getProduct,
    getProductPreview,
    getPackage: getPackageVersions,
    claimUpmEntitlement,
    checkEntitlement,
    reportReview,
    lookupProductLists,
  };
}
